import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Snackbar, Alert, CircularProgress, Box } from '@mui/material';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import Expenses from './pages/Expenses';
import Bloomberg from './pages/Bloomberg';
import Reports from './pages/Reports';
import Operations from './pages/Operations';
import Holders from './pages/Holders';
import Beneficiaries from './pages/Beneficiaries';
import Jurisdictions from './pages/Jurisdictions';
import Accounts from './pages/Accounts';
import RealEstate from './pages/RealEstate';
import Businesses from './pages/Businesses';
import Settings from './pages/Settings';
import Login from './pages/Login';
import { authApi } from './services/api';

// Create context for global state
export const AppContext = createContext();

export const useApp = () => useContext(AppContext);

// Notification component
const Notification = ({ notification, onClose }) => {
  if (!notification) return null;

  return (
    <Snackbar
      open={!!notification}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={onClose} severity={notification.type} variant="filled" sx={{ width: '100%' }}>
        {notification.message}
      </Alert>
    </Snackbar>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useApp();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [workspace, setWorkspace] = useState('all'); // 'all', 'ru', 'non-ru'

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authApi.me();
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authApi.login(username, password);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      showNotification('Успешный вход в систему', 'success');
      return true;
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка входа', 'error');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    showNotification('Вы вышли из системы', 'info');
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const contextValue = {
    user,
    loading,
    login,
    logout,
    showNotification,
    workspace,
    setWorkspace,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/assets" element={<Assets />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/bloomberg" element={<Bloomberg />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/operations" element={<Operations />} />
                  <Route path="/holders" element={<Holders />} />
                  <Route path="/beneficiaries" element={<Beneficiaries />} />
                  <Route path="/jurisdictions" element={<Jurisdictions />} />
                  <Route path="/accounts" element={<Accounts />} />
                  <Route path="/real-estate" element={<RealEstate />} />
                  <Route path="/businesses" element={<Businesses />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Notification notification={notification} onClose={closeNotification} />
    </AppContext.Provider>
  );
}

export default App;
